import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, useTheme } from '@mui/joy';

const AnimatedPieChart = ({ 
  data, 
  width = '100%', 
  height = 300, 
  colors = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#84cc16'],
  showLabels = true,
  showValues = false,
  title,
  subtitle,
  innerRadius = 0.4
}) => {
  const theme = useTheme();
  const [hoveredSlice, setHoveredSlice] = useState(null);
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        width, 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        borderRadius: 2,
        textAlign: 'center'
      }}>
        <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center' }}>
          No data available
        </Typography>
      </Box>
    );
  }

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(centerX, centerY) - 60;
  const outerRadius = radius;
  const innerRadiusValue = innerRadius * radius;

  let currentAngle = -Math.PI / 2; // Start from top

  const createArc = (startAngle, endAngle, outerR, innerR) => {
    const x1 = centerX + Math.cos(startAngle) * outerR;
    const y1 = centerY + Math.sin(startAngle) * outerR;
    const x2 = centerX + Math.cos(endAngle) * outerR;
    const y2 = centerY + Math.sin(endAngle) * outerR;

    const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;

    const outerArc = `M ${x1} ${y1} A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
    const innerArc = `L ${centerX + Math.cos(endAngle) * innerR} ${centerY + Math.sin(endAngle) * innerR} A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${centerX + Math.cos(startAngle) * innerR} ${centerY + Math.sin(startAngle) * innerR} Z`;

    return outerArc + innerArc;
  };

  // Prepare slices for z-ordering
  let slices = [];
  let hovered = null;
  currentAngle = -Math.PI / 2;
  data.forEach((item, index) => {
    const sliceAngle = (item.value / total) * 2 * Math.PI;
    const startAngle = currentAngle;
    const endAngle = currentAngle + sliceAngle;
    const midAngle = startAngle + sliceAngle / 2;
    const color = colors[index % colors.length];
    const isHovered = hoveredSlice === index;
    const sliceInfo = { item, index, startAngle, endAngle, midAngle, color, isHovered };
    if (isHovered) {
      hovered = sliceInfo;
    } else {
      slices.push(sliceInfo);
    }
    currentAngle = endAngle;
  });

  return (
    <Box sx={{ width, height: height + (title ? 40 : 0) }}>
      {title && (
        <Box sx={{ mb: 1 }}>
          <Typography level="title-sm" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      {/* Centered chart container */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height }}>
        <Box sx={{ position: 'relative', width, height }}>
          <svg width={width} height={height} style={{ overflow: 'visible' }}>
            {/* Pie Slices */}
            {data.map((item, index) => {
              const sliceAngle = (item.value / total) * 2 * Math.PI;
              const startAngle = index === 0 ? -Math.PI / 2 : undefined;
              let prevAngle = -Math.PI / 2;
              for (let i = 0; i < index; i++) {
                prevAngle += (data[i].value / total) * 2 * Math.PI;
              }
              const actualStartAngle = startAngle !== undefined ? startAngle : prevAngle;
              const endAngle = actualStartAngle + sliceAngle;
              const midAngle = actualStartAngle + sliceAngle / 2;
              const color = colors[index % colors.length];
              const isHovered = hoveredSlice === index;
              // Calculate pop-out translation for hover
              const popOutDist = isHovered ? 8 : 0; // px
              const translateX = Math.cos(midAngle) * popOutDist;
              const translateY = Math.sin(midAngle) * popOutDist;
              return (
                <motion.g key={index} layoutId={`slice-${index}`}
                  style={{ zIndex: isHovered ? 2 : 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 30,
                    mass: 1,
                    duration: 0.4
                  }}
                >
                  <motion.path
                    d={createArc(actualStartAngle, endAngle, outerRadius, innerRadiusValue)}
                    fill={color}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{
                      scale: 1,
                      opacity: 1,
                      filter: isHovered ? 'brightness(1.1)' : 'none'
                    }}
                    whileHover={{
                      translateX: Math.cos(midAngle) * 8,
                      translateY: Math.sin(midAngle) * 8,
                      filter: 'brightness(1.1)'
                    }}
                    transition={{
                      type: 'spring',
                      stiffness: 400,
                      damping: 30,
                      mass: 1,
                      duration: 0.4
                    }}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredSlice(index)}
                    onMouseLeave={() => setHoveredSlice(null)}
                  />
                </motion.g>
              );
            })}
            {/* Tooltip always rendered last, above all */}
            {hoveredSlice !== null && (() => {
              let prevAngle = -Math.PI / 2;
              for (let i = 0; i < hoveredSlice; i++) {
                prevAngle += (data[i].value / total) * 2 * Math.PI;
              }
              const sliceAngle = (data[hoveredSlice].value / total) * 2 * Math.PI;
              const startAngle = prevAngle;
              const midAngle = startAngle + sliceAngle / 2;
              const label = data[hoveredSlice].label;
              const percent = Math.round((data[hoveredSlice].value / total) * 100);
              const maxLabelLength = 16;
              const displayLabel = label.length > maxLabelLength
                ? label.slice(0, maxLabelLength - 1) + '…'
                : label;
              return (
                <motion.g
                  key="tooltip"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30, mass: 1, duration: 0.25 }}
                  style={{ pointerEvents: 'none' }}
                >
                  <rect
                    x={centerX + Math.cos(midAngle) * (outerRadius * 0.8) - 60}
                    y={centerY + Math.sin(midAngle) * (outerRadius * 0.8) - 20}
                    width={120}
                    height={40}
                    rx={8}
                    fill={theme.palette.neutral[900]}
                    opacity={0.92}
                  />
                  <text
                    x={centerX + Math.cos(midAngle) * (outerRadius * 0.8)}
                    y={centerY + Math.sin(midAngle) * (outerRadius * 0.8) - 4}
                    fontSize="11"
                    fill="white"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontWeight="500"
                  >
                    {displayLabel}
                  </text>
                  <text
                    x={centerX + Math.cos(midAngle) * (outerRadius * 0.8)}
                    y={centerY + Math.sin(midAngle) * (outerRadius * 0.8) + 10}
                    fontSize="13"
                    fill="white"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontWeight="bold"
                  >
                    {percent}%
                  </text>
                </motion.g>
              );
            })()}
          </svg>
        </Box>
      </Box>
    </Box>
  );
};

export default AnimatedPieChart; 